import { Dispatch, SetStateAction, useState } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/system"
import { ColorOption, Package, ProductChoiceType, ProductOrderLine, SelectOption } from "../../../shopsTypes"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { BuyerParty } from "../../../../../../payments/orderTypes"
import { SelectTabletSize } from "./SelectTabletSize"
import { PackageCounter } from "./PackageCounter"
import { SelectColor } from "./SelectColor"

const useStyles = makeStyles()((theme) => ({
    lightColor: {
        color: theme.palette.text.secondary,
    },
    baseColor: {
        color: theme.palette.text.primary,
    },
}))

type ProductContentProps = {
    product: Package
    selectedPackages: ProductOrderLine[]
    setSelectedPackages: Dispatch<SetStateAction<ProductOrderLine[]>>
}

export const ProductContent = ({ product, selectedPackages, setSelectedPackages }: ProductContentProps) => {
    const { classes } = useStyles()
    const productChoices = product.productChoices[0]
    const isColorOptions = productChoices.type === ProductChoiceType.COLOR_SWATCH

    const [selectedColor, setSelectedColor] = useState<ColorOption | undefined>(
        isColorOptions ? productChoices.colorOptions[0] : undefined,
    )
    const [selectedTabletSize, setSelectedTabletSize] = useState<SelectOption | undefined>(
        !isColorOptions ? productChoices.selectOptions[0] : undefined,
    )
    const [items, setItems] = useState(1)

    const checkIfProductExists = (productId: number) => {
        return selectedPackages.some((pkg) => pkg.productId === productId)
    }

    const handleAddPackage = () => {
        const productChoicesItem =
            isColorOptions && selectedColor
                ? [{ id: selectedColor.color, value: selectedColor.value }]
                : selectedTabletSize
                ? [{ id: selectedTabletSize.label, value: selectedTabletSize.value }]
                : []
        setSelectedPackages((prev) => [
            ...prev,
            {
                productId: product.id,
                items: items,
                buyerParty: BuyerParty.COMPANY,
                productChoices: productChoicesItem,
            },
        ])
    }

    const handleCounter = (el: number) => {
        if (!el) {
            setSelectedPackages((prevPackages) => {
                return prevPackages.filter((pkg) => pkg.productId !== product.id)
            })
            setItems(1)
        } else {
            const updatedPackages = selectedPackages.map((pkg) =>
                pkg.productId === product.id ? { ...pkg, items: el } : pkg,
            )
            setSelectedPackages(updatedPackages)

            setItems(el)
        }
    }

    // Function to handle selecting color
    const handleSelectColor = (colorOption: ColorOption) => {
        if (checkIfProductExists(product.id)) {
            const updatedPackages = selectedPackages.map((pkg) =>
                pkg.productId === product.id
                    ? { ...pkg, productChoices: [{ id: colorOption.color, value: colorOption.value }] }
                    : pkg,
            )
            setSelectedPackages(updatedPackages)
        }

        setSelectedColor(colorOption)
    }

    const handleSelectTabletSize = (tabletOption: SelectOption) => {
        if (checkIfProductExists(product.id)) {
            const updatedPackages = selectedPackages.map((pkg) =>
                pkg.productId === product.id
                    ? { ...pkg, productChoices: [{ id: tabletOption.label, value: tabletOption.value }] }
                    : pkg,
            )
            setSelectedPackages(updatedPackages)
        }
        setSelectedTabletSize(tabletOption)
    }

    return (
        <Stack direction="column" spacing={isColorOptions ? 7 : 3}>
            <Stack direction="column" spacing={2}>
                <Typography variant="body1" className={classes.baseColor}>
                    {product.name}
                </Typography>
                <Typography variant="body2" className={classes.lightColor}>
                    {product.description}
                </Typography>
                {productChoices.type === ProductChoiceType.COLOR_SWATCH ? (
                    <SelectColor
                        list={productChoices.colorOptions}
                        selectedColor={selectedColor}
                        handleSelectColor={handleSelectColor}
                    />
                ) : (
                    <SelectTabletSize
                        list={productChoices.selectOptions}
                        selectedTabletSize={selectedTabletSize}
                        handleSelectTabletSize={handleSelectTabletSize}
                    />
                )}
            </Stack>
            {checkIfProductExists(product.id) ? (
                <PackageCounter count={items} handleCounter={handleCounter} />
            ) : (
                <Box>
                    <StandardButton size="medium" fullWidth={false} onClick={() => handleAddPackage()}>
                        Add
                    </StandardButton>
                </Box>
            )}
        </Stack>
    )
}
